import { Button, type ButtonProps as RACButtonProps } from 'components/ui/button';
import { Drawer, DrawerRoot, DrawerHeader } from 'components/ui/drawer';
import { useI18n } from 'context/i18n';
import { composeRenderProps, DialogTrigger } from 'react-aria-components';
import type { TFilter, TFilterSection } from 'types/product-list';
import type { FilterState } from './use-filters';
import { Button as RacButton } from 'react-aria-components';
import type { PropsWithChildren } from 'react';
import { filterColors, sorts, type TSort } from 'utils/filters';
import { useLocale } from 'hooks';
import { getCountryFromMarket } from 'utils/translations';
import { tv } from 'tailwind-variants';
import { focusRing } from 'components/ui/component-utils';

type FilterToggleButtonProps = RACButtonProps & {
  active: boolean;
  available: boolean;
};

export function ColorSquare({ colorId, color }: { colorId: string; color?: string }) {
  const isWhite = color === '#FFFFFF' ? true : colorId === 'C02';
  return <div style={{ background: color ?? filterColors[colorId] }} className={`size-2.5 border ${isWhite ? 'border-dark' : 'border-clear'}`} />;
}

const tg = tv({
  extend: focusRing,
  base: 'flex h-8 w-auto min-w-8 items-center justify-start gap-3 border px-2.5 text-sm font-bold capitalize transition lg:h-6 lg:px-2 lg:text-xs',
  variants: {
    active: {
      true: '',
      false: '',
    },
    available: {
      true: '',
      false: 'border-subtle text-disabled',
    },
  },
  compoundVariants: [
    {
      active: true,
      available: true,
      className: 'border-dark text-copy',
    },
    {
      active: false,
      available: true,
      className: 'border-subtle text-copy hover:border-dark pressed:border-dark',
    },
  ],
});

export function FilterToggleButton({ children, active, available, ...rest }: FilterToggleButtonProps) {
  return (
    <RacButton
      data-active={active}
      className={composeRenderProps('', (className, renderProps) => tg({ ...renderProps, active, available, className }))}
      {...rest}
    >
      {children}
    </RacButton>
  );
}

type FilterSectionProps = PropsWithChildren<{
  title: string;
  type: 'grid-2' | 'grid-auto';
}>;

export function FilterSection({ title, type, children }: FilterSectionProps) {
  const gridClasses = {
    'grid-2': 'grid grid-cols-2',
    'grid-auto': 'grid grid-cols-6',
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm uppercase lg:text-xs">{title}</div>
      <div className={`gap-2 ${gridClasses[type]}`}>{children}</div>
    </div>
  );
}

type CamperlabFiltersProps = {
  filters: TFilterSection[];
  state: FilterState;
  filtersCount: number;
  onApply: (filters: FilterState['active'], sort?: TSort) => void;
  onClose: (visible: boolean, fakeApply?: boolean) => void;
  onRemove: (params: { parent: string; key: string }) => void;
  onSort: (sort: TSort) => void;
  onReset: () => void;
};

export function CamperlabFilters({ filters, state, filtersCount, onApply, onClose, onRemove, onSort, onReset }: CamperlabFiltersProps) {
  const { t } = useI18n();
  const { country: market } = useLocale();
  const country = getCountryFromMarket(market);

  const handleClickFilter = ({ parent, filter, multiple }: { parent: string; filter: TFilter; multiple: boolean }) => {
    const newFilters = { ...state.active };
    // si no existe el parent lo creamos
    if (!newFilters[parent]) {
      newFilters[parent] = new Map<string, TFilter>();
    }
    if (!multiple) {
      // si no es multiple lo vaciamos y añadimos el filtro
      if (newFilters[parent].size > 0) {
        newFilters[parent].clear();
      }
      newFilters[parent].set(filter.key, filter);
    }
    if (multiple) {
      // si es multiple, si ya existe el filtro lo eliminamos, si no lo añadimos
      if (newFilters[parent].has(filter.key)) {
        newFilters[parent].delete(filter.key);
      } else {
        newFilters[parent].set(filter.key, filter);
      }
    }
    onApply(newFilters, state.sort);
  };

  return (
    <DialogTrigger isOpen={state.open} onOpenChange={(isOpen) => onClose(isOpen)}>
      <Button variant="text" className="h-6 flex-shrink-0 text-sm font-bold uppercase md:text-xs">
        {t('menu.lateral', 'filtrar', 'Filter')} ( {String(filtersCount)} )
      </Button>
      <DrawerRoot className="w-full">
        <Drawer>
          <div className="relative flex h-full max-h-[100dvh] flex-grow flex-col overflow-x-hidden">
            <DrawerHeader label={t('menu.lateral', 'filtros', 'Filtros')} className="px-2.5" />
            <div className="flex-grow px-2.5">
              <div className="flex flex-shrink-0 flex-col gap-10">
                <FilterSection title={t('menu.lateral', 'ordenar.por')} type="grid-2">
                  {Object.keys(sorts).map((sortValue) => (
                    <FilterToggleButton
                      key={sortValue}
                      active={state.sort === sortValue}
                      available={true}
                      isDisabled={state.sort === sortValue}
                      onPress={() => onSort(sortValue as TSort)}
                    >
                      {t('menu.lateral', sorts[sortValue], sortValue)}
                    </FilterToggleButton>
                  ))}
                </FilterSection>
                {filters.reduce((acc, { title, id, options, multiple }) => {
                  if (id === 'collections') {
                    return acc;
                  }
                  acc.push(
                    <FilterSection key={id} title={id !== 'sizes' ? t(...title) : `${t(...title)} (${country})`} type={id === 'sizes' ? 'grid-auto' : 'grid-2'}>
                      {options.map((filter) => {
                        return (
                          <FilterToggleButton
                            key={`${id}-${filter.key}`}
                            active={state.active[id] && state.active[id].has(filter.key)}
                            data-filter={filter.key}
                            available={state.available[id] && state.available[id].has(filter.key)}
                            onPress={() =>
                              handleClickFilter({
                                parent: id,
                                filter,
                                multiple,
                              })
                            }
                          >
                            {id === 'colors' ?
                              <ColorSquare colorId={filter.key} />
                            : null}
                            {filter.label !== '' ? filter.label : filter.urlParam}
                          </FilterToggleButton>
                        );
                      })}
                    </FilterSection>,
                  );

                  return acc;
                }, [])}
              </div>
              <div className="h-[100px]"></div>
            </div>
            <div className="sticky bottom-0 flex flex-shrink-0 flex-col gap-2.5 bg-clear p-2.5">
              <Button variant="solid" onPress={() => onClose(false, true)} className="h-8 justify-start px-2.5 text-left text-sm font-bold uppercase md:hidden">
                {t('menu.lateral', 'aplicar', 'Apply')}
              </Button>
              <Button
                variant="outline"
                onPress={() => {
                  onReset();
                }}
              >
                {t('menu.lateral', 'limpiar', 'Clear')}
              </Button>
            </div>
          </div>
        </Drawer>
      </DrawerRoot>
    </DialogTrigger>
  );
}
